import { useState } from 'react';
import type {
  ScreenshotItem,
  ScreenshotSelection,
} from './agentSessionScreenshots';
import type { AgentSessionTab } from './agentSessionTabs';

interface NavigationProps {
  runId: string | null;
  focusRequest: { messageId: string } | null;
  defaultTab: AgentSessionTab;
}

interface NavigationState {
  runId: string | null;
  request: NavigationProps['focusRequest'];
  activeTab: AgentSessionTab;
  selection: ScreenshotSelection | null;
}

function requestedSelection(
  request: NavigationProps['focusRequest'],
): ScreenshotSelection | null {
  return request ? { kind: 'message', id: request.messageId } : null;
}

function isAgentSessionTab(value: string): value is AgentSessionTab {
  return (
    value === 'live-watch' ||
    value === 'recording' ||
    value === 'screenshots' ||
    value === 'events'
  );
}

/** Consume each trace/evidence request once; later navigation belongs to the user. */
export function useAgentSessionNavigation({
  runId,
  focusRequest,
  defaultTab,
}: NavigationProps) {
  const [state, setState] = useState<NavigationState>(() => ({
    runId,
    request: focusRequest,
    activeTab: focusRequest ? 'screenshots' : defaultTab,
    selection: requestedSelection(focusRequest),
  }));

  // Adjust before children render, so neither a poll nor an old request can
  // re-focus the gallery after the user has navigated away.
  const runChanged = state.runId !== runId;
  const requestChanged = state.request !== focusRequest;
  if (runChanged || requestChanged) {
    const request = requestChanged ? focusRequest : null;
    setState({
      runId,
      request: focusRequest,
      activeTab: request
        ? 'screenshots'
        : runChanged
          ? defaultTab
          : state.activeTab,
      selection: requestedSelection(request),
    });
  }

  function selectTab(value: string) {
    if (!isAgentSessionTab(value)) return;
    setState((current) => ({ ...current, activeTab: value, selection: null }));
  }

  function selectScreenshot(item: ScreenshotItem) {
    setState((current) => ({
      ...current,
      activeTab: 'screenshots',
      selection: { kind: 'item', id: item.id },
    }));
  }

  return { ...state, selectTab, selectScreenshot };
}

export type { AgentSessionTab };
